/**
 * PaperCraneSystem — client-side handler for paper crane arrival and inbox.
 *
 * AGE-1475 Round 14 Phase C:
 *   - MSG_CRANE_ARRIVED → shows a brief toast notification at the bottom of screen.
 *   - MSG_CRANE_INBOX  → stores the delivered crane list in the game registry and
 *                         renders a small unread-count badge in the HUD.
 *
 * Follows the FestivalToast pattern: scene-owned, destroy()-able, no global state.
 */

import * as Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  MSG_CRANE_ARRIVED,
  MSG_CRANE_INBOX,
  type CraneArrivedEvent,
  type CraneInboxPayload,
  type CraneInboxEntry,
} from '@game/shared';

const TOAST_HOLD_MS = 4000;
const TOAST_FADE_MS = 700;
const TOAST_BOTTOM_OFFSET = 90;
const TOAST_DEPTH = 201;

const BADGE_DEPTH = 202;
const BADGE_BOTTOM_OFFSET = 40;
const BADGE_RIGHT_OFFSET = 12;

export class PaperCraneSystem {
  private scene: Phaser.Scene;
  private room: Room | null;

  private toastText: Phaser.GameObjects.Text | null = null;
  private activeTween: Phaser.Tweens.Tween | null = null;

  private badgeText: Phaser.GameObjects.Text | null = null;
  private unreadCount = 0;

  constructor(scene: Phaser.Scene, room: Room) {
    this.scene = scene;
    this.room = room;
    this.wireListeners();
    this.ensureBadge();
  }

  private wireListeners(): void {
    if (!this.room) return;

    this.room.onMessage(MSG_CRANE_ARRIVED, (raw: unknown) => {
      const evt = raw as CraneArrivedEvent;
      this.handleCraneArrived(evt);
    });

    this.room.onMessage(MSG_CRANE_INBOX, (raw: unknown) => {
      const payload = raw as CraneInboxPayload;
      this.handleCraneInbox(payload);
    });
  }

  private handleCraneArrived(evt: CraneArrivedEvent): void {
    const from = evt.senderName || 'a stranger';
    const text = evt.hasGift
      ? `A paper crane arrived from ${from} — with a gift!`
      : `A paper crane arrived from ${from}.`;
    this.showToast(text);

    // Increment unread count for the newly arrived crane.
    this.unreadCount++;
    this.refreshBadge();
  }

  private handleCraneInbox(payload: CraneInboxPayload): void {
    const cranes: CraneInboxEntry[] = Array.isArray(payload.cranes) ? payload.cranes : [];
    this.scene.game.registry.set('crane.inbox', cranes);

    this.unreadCount = cranes.filter((c) => c.readAt === null).length;
    this.refreshBadge();
  }

  private showToast(message: string): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }

    const cam = this.scene.cameras.main;
    const x = cam.width / 2;
    const y = cam.height - TOAST_BOTTOM_OFFSET;

    this.toastText = this.scene.add.text(x, y, message, {
      fontSize: '14px',
      color: '#f0e6c8',
      fontFamily: 'serif',
      align: 'center',
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true, stroke: true },
    });
    this.toastText.setOrigin(0.5, 0.5);
    this.toastText.setAlpha(0);
    this.toastText.setDepth(TOAST_DEPTH);
    this.toastText.setScrollFactor(0);

    this.activeTween = this.scene.tweens.add({
      targets: this.toastText,
      alpha: { from: 0, to: 1 },
      duration: TOAST_FADE_MS,
      ease: 'Sine.easeIn',
      hold: TOAST_HOLD_MS,
      yoyo: true,
      onComplete: () => {
        this.toastText?.destroy();
        this.toastText = null;
        this.activeTween = null;
      },
    });
  }

  private ensureBadge(): void {
    const cam = this.scene.cameras.main;
    const x = cam.width - BADGE_RIGHT_OFFSET;
    const y = cam.height - BADGE_BOTTOM_OFFSET;

    this.badgeText = this.scene.add.text(x, y, '', {
      fontSize: '12px',
      color: '#d4b896',
      fontFamily: 'monospace',
      align: 'right',
    });
    this.badgeText.setOrigin(1, 0.5);
    this.badgeText.setDepth(BADGE_DEPTH);
    this.badgeText.setScrollFactor(0);
    this.badgeText.setVisible(false);
  }

  private refreshBadge(): void {
    if (!this.badgeText) return;
    if (this.unreadCount <= 0) {
      this.badgeText.setVisible(false);
      return;
    }
    const label = this.unreadCount === 1 ? '1 crane unread' : `${this.unreadCount} cranes unread`;
    this.badgeText.setText(label);
    this.badgeText.setVisible(true);
  }

  destroy(): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }
    if (this.badgeText) {
      this.badgeText.destroy();
      this.badgeText = null;
    }
    this.room = null;
  }
}
